import React, { useRef, useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform, useWindowDimensions, Modal, Animated } from 'react-native';
import { platformAlert } from '../utils/platformAlert';
import { logger } from '../utils/logger';

// Max width for phone container on web
const PHONE_CONTAINER_MAX_WIDTH = 430;
import { Image } from 'expo-image';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useColors } from '../hooks/useColors';
import { Button } from '../components/ui/Button';
import { useProduct } from '../hooks/useProduct';
import { useVendors } from '../hooks/useVendors';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { useCart } from '../hooks/useCart';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '@react-navigation/native';
import { CATEGORIES } from '../lib/categories';
import { spacing, typography } from '../theme';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { formatDisplayValue, formatPrice } from '../utils/format';
import { findOrCreateChat } from '../utils/chatUtils';
import { usePostHog } from '../hooks/usePostHog';
import { trackEvent } from '../services/posthog';
import { VendorRatingsModal } from '../components/vendors/VendorRatingsModal';
import { useVendorRatings } from '../hooks/useVendorRatings';
import { isStoreCurrentlyOpen, type StoreHours } from '../utils/storeHours';
import { StoreHoursDisplay } from '../components/vendors/StoreHoursDisplay';
import { useGuestMode } from '../contexts/GuestModeContext';
import { useLikes } from '../hooks/useLikes';

type ProductDetailsRouteProp = RouteProp<RootStackParamList, 'ProductDetails'>;

interface ProductDetailsProps {
  route: ProductDetailsRouteProp;
}

export default function ProductDetailsScreen({ route }: ProductDetailsProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const { user } = useAuth();
  const { requireAuth } = useGuestMode();
  const { id } = route.params;

  // Animations - start at final values to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  
  // Get responsive window dimensions
  const windowDimensions = useWindowDimensions();
  
  // On web, constrain width to phone container max width
  const effectiveWidth = Platform.OS === 'web' 
    ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
    : windowDimensions.width;
  const { data: product, isLoading, error } = useProduct(id);
  const { data: vendors } = useVendors();
  const vendorProfile = React.useMemo(() => {
    return vendors?.find(v => v.id === product?.vendor_id || v.id === product?.vendor?.id);
  }, [vendors, product?.vendor_id, product?.vendor?.id]);
  const [selectedSize, setSelectedSize] = React.useState<string>();
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [isStartingChat, setIsStartingChat] = React.useState(false);
  const [quantity, setQuantity] = React.useState(1);
  const [showRatingsModal, setShowRatingsModal] = React.useState(false);
  const [showStoreHoursModal, setShowStoreHoursModal] = React.useState(false);
  
  // Fetch vendor ratings
  const { data: ratingsData } = useVendorRatings(product?.vendor?.id);
  
  // Fetch vendor delivery hours and location directly (more reliable than vendorProfile)
  const { data: vendorDetailsData } = useQuery({
    queryKey: ['vendor-details', product?.vendor?.id],
    queryFn: async () => {
      if (!product?.vendor?.id) return null;
      const { data, error } = await supabase
        .from('vendors')
        .select('delivery_hours, city, state, country')
        .eq('id', product.vendor.id)
        .single();
      if (error) {
        console.error('Error fetching vendor details:', error);
        return null;
      }
      return data;
    },
    enabled: !!product?.vendor?.id,
  });
  
  // Extract vendor hours and location for timezone handling
  const vendorHoursData = vendorDetailsData?.delivery_hours;
  const vendorLocation = {
    city: vendorDetailsData?.city,
    state: vendorDetailsData?.state,
    country: vendorDetailsData?.country,
  };
  
  // Calculate store open status (using store's timezone)
  const storeStatus = React.useMemo(() => {
    const deliveryHours = vendorHoursData as StoreHours | undefined;
    return isStoreCurrentlyOpen(deliveryHours, vendorLocation);
  }, [vendorHoursData, vendorLocation]);
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const hasTrackedViewRef = React.useRef(false);
  const { toggleLike, isLiked } = useLikes();
  const liked = isLiked(id);

  const handleLike = async () => {
    if (!requireAuth('like products')) {
      return;
    }
    await toggleLike(id);
  };

  // Check if current user is the vendor
  const isVendor = React.useMemo(() => {
    logger.debug('ProductDetails', 'Checking vendor status', {
      userId: user?.id,
      vendorUserId: product?.vendor?.user_id,
      isMatch: user?.id === product?.vendor?.user_id
    });
    return user?.id === product?.vendor?.user_id;
  }, [user?.id, product?.vendor?.user_id]);

  //TODO - might need to come back to this later to fix the product view.
  React.useEffect(() => {
    if (product) {
      logger.debug('ProductDetails', 'Product loaded', {
        productId: product.id,
        vendorId: product.vendor?.id,
        vendorUserId: product.vendor?.user_id,
        currentUserId: user?.id,
        isVendor
      });

      // Track product view analytics
      const category = CATEGORIES.find(cat => 
        cat.subcategories.some(sub => sub.id === product.category_subcategory)
      )?.name || product.category_subcategory;
      
      // Track product view once per mount
      if (!hasTrackedViewRef.current) {
        try {
          trackEvent(
            'product_viewed',
            {
              item_id: product.id,
              item_name: product.title,
              price: typeof product.price === 'number' ? product.price : undefined,
              category,
              vendor_id: product.vendor?.id,
            },
            posthog || undefined
          );
          hasTrackedViewRef.current = true;
        } catch (analyticsError) {
          logger.warn('ProductDetails', 'Failed to track product_viewed', analyticsError);
        }
      }
    }
  }, [product, user?.id, isVendor, posthog]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load product</Text>
      </View>
    );
  }

  const renderImage = () => {
    if (!product.images?.length) {
      return (
        <View style={[styles.imageContainer, styles.fallbackImage]}>
          <Feather name="image" size={48} color={colors.textLight} />
        </View>
      );
    }

    return (
      <View style={styles.imageContainer}>
        <ScrollView
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={(event) => {
            const slideSize = event.nativeEvent.layoutMeasurement.width;
            const index = Math.round(event.nativeEvent.contentOffset.x / slideSize);
            setActiveImageIndex(index);
          }}
          scrollEventThrottle={200}
        >
          {product.images.map((image, index) => (
            <Image
              key={index}
              source={{ uri: image }}
              style={[
                styles.image,
                {
                  width: effectiveWidth,
                  backgroundColor: colors.background,
                }
              ]}
              contentFit="contain"
              cachePolicy="memory-disk"
              transition={0}
            />
          ))}
        </ScrollView>
        {product.images.length > 1 && (
          <View style={styles.pagination}>
            {product.images.map((_, index) => (
              <View
                key={index}
                style={[
                  styles.paginationDot,
                  index === activeImageIndex && styles.paginationDotActive
                ]}
              />
            ))}
          </View>
        )}
      </View>
    );
  };

  const handleAddToCart = async () => {
    try {
      if (!product) return;

      // Guard: prevent adding when no stock
      const availableUnits = Number(product.quantity ?? 0);
      if (!Number.isFinite(availableUnits) || availableUnits <= 0) {
        showToast('This item is sold out');
        return;
      }

      logger.debug('Cart', 'Adding product to cart with stock guard', { productId: product.id, availableUnits });

      // Check auth - show modal if guest
      if (!requireAuth('add items to cart')) {
        return;
      }

      // Get session for user ID
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        logger.debug('Cart', 'User not authenticated');
        return;
      }

      const userId = session.user.id;

      // Fetch at most one row; handle "no rows" and "multiple rows" explicitly
      const { data: existingRow, error: existingErr } = await supabase
        .from('cart_items')
        .select('id, quantity')
        .eq('user_id', userId)
        .eq('product_id', product.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingErr) {
        // PGRST116 = No rows; PGRST122 = Multiple rows; other codes may indicate RLS
        const code = (existingErr as any)?.code;
        if (code === 'PGRST116') {
          // no rows found is OK, we will insert
          logger.debug('Cart', 'No existing cart row; will insert new row');
        } else if (code === 'PGRST122' || /more than one row/i.test(String(existingErr?.message))) {
          logger.warn('Cart', 'Multiple cart rows found for user/product; proceeding with latest row only. Consider adding a UNIQUE(user_id, product_id) constraint.');
        } else {
          logger.error('Cart', 'Error fetching existing cart row (possible RLS/policy)', existingErr);
        }
      }

      const existingQty = Number(existingRow?.quantity ?? 0);
      
      // If user already has max available in cart, show message
      if (existingQty >= availableUnits) {
        showToast('Maximum quantity already in cart');
        return;
      }
      
      // Cap the quantity to what's actually available
      const qtyToAdd = Math.min(quantity, availableUnits - existingQty);
      const nextQty = existingQty + qtyToAdd;

      // Write: insert or update with capped quantity
      let writeError: any = null;
      if (existingRow?.id) {
        const { error: updErr } = await supabase
          .from('cart_items')
          .update({ quantity: nextQty })
          .eq('id', existingRow.id);
        writeError = updErr;
      } else {
        const { error: insErr } = await supabase
          .from('cart_items')
          .insert({ product_id: product.id, user_id: userId, quantity: qtyToAdd });
        writeError = insErr;
      }

      if (writeError) {
        logger.error('Cart', 'Error writing cart row', writeError);
        showToast('Failed to add item to cart');
        return;
      }

      // Success
      showToast('Added to cart');

      // Refresh cart queries
      queryClient.invalidateQueries({ queryKey: ['cart', userId] });
    } catch (error: any) {
      logger.error('Cart', 'Final error adding to cart (stock guard)', error);
      showToast('Failed to add item to cart: ' + (error?.message || 'Unknown error'));
    }
  };

  const getCategoryInfo = (product) => {
    if (!product?.category_main) return null;
    
    const category = CATEGORIES.find(c => c.id === product.category_main);
    if (!category) return null;

    const subcategory = category.subcategories.find(s => s.id === product.category_subcategory);
    
    return {
      category: category.name,
      subcategory: subcategory?.name,
      item: formatDisplayValue(product.category_item)
    };
  };

  const startChat = async () => {
    // Validate prerequisites
    if (!product?.vendor || !product.vendor.id || !product.vendor.user_id) {
      platformAlert("Error", "Unable to start chat - vendor information is missing.");
      return;
    }

    // Check auth - show modal if guest
    if (!requireAuth('message sellers')) {
      return;
    }

    if (!user) {
      return;
    }

    if (product.vendor.user_id === user.id) {
      platformAlert("Error", "You cannot message yourself.");
      return;
    }

    setIsStartingChat(true);

    try {
      logger.debug('Chat', `Starting chat between user ${user.id} and vendor user ${product.vendor.user_id} (vendor ${product.vendor.id})`);

      // Find or create chat using user IDs (not vendor_id) to ensure consistent chat grouping
      const chatId = await findOrCreateChat(user.id, product.vendor.user_id, product.vendor.id);
      
      logger.debug('Chat', `Navigating to chat: ${chatId}`);
      navigation.navigate("Chat", { id: chatId });
      
    } catch (error) {
      logger.error('Chat', 'Error starting chat', error);
      platformAlert(
        "Chat Error",
        error instanceof Error ? error.message : "Unable to start chat. Please try again.",
        [{ text: "OK" }]
      );
    } finally {
      setIsStartingChat(false);
    }
  };

  return (
    <View style={styles.container}>
      <Animated.View 
        style={[
          styles.container,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          <View style={styles.imageSection}>
            {renderImage()}
            <TouchableOpacity
              style={[styles.likeButton, liked && styles.likeButtonActive]}
              onPress={handleLike}
              activeOpacity={0.8}
            >
              <Feather
                name="heart"
                size={18}
                color={liked ? "#000" : colors.text}
                fill={liked ? "#000" : "none"}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.mainContent}>
            <View style={styles.headerCard}>
              <View style={styles.productInfo}>
                <Text style={styles.title}>{product.title}</Text>
                <View style={styles.priceContainer}>
                  <Text style={styles.price}>{formatPrice(product.price)}</Text>
                </View>
              </View>

              <View style={styles.headerDivider} />

              <TouchableOpacity 
                style={styles.vendorHeader}
                onPress={() => navigation.navigate('Vendor', { id: product.vendor.id })}
                activeOpacity={0.7}
              >
                {product.vendor.logo_image ? (
                  <Image 
                    source={{ uri: product.vendor.logo_image }} 
                    style={styles.vendorLogo}
                    contentFit="cover"
                  />
                ) : (
                  <View style={styles.vendorLogoPlaceholder}>
                    <Feather name="store" size={18} color={colors.textLight} />
                  </View>
                )}
                <View style={styles.vendorNameContainer}>
                  <Text style={styles.vendorName} numberOfLines={1}>{product.vendor.store_name}</Text>
                  <View style={styles.vendorSubHeader}>
                    <TouchableOpacity 
                      style={styles.ratingInline}
                      onPress={() => setShowRatingsModal(true)}
                    >
                      <Ionicons name="star" size={12} color="#FFD700" />
                      <Text style={styles.ratingTextInline}>
                        {ratingsData?.averageRating ? ratingsData.averageRating.toFixed(1) : '—'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[
                        styles.storeStatusBadge,
                        storeStatus.isOpen ? styles.storeStatusOpen : styles.storeStatusClosed
                      ]}
                      onPress={() => setShowStoreHoursModal(true)}
                      activeOpacity={0.7}
                    >
                      <View style={[
                        styles.storeStatusDot,
                        storeStatus.isOpen ? styles.storeStatusDotOpen : styles.storeStatusDotClosed
                      ]} />
                      <Text style={[
                        styles.storeStatusText,
                        storeStatus.isOpen ? styles.storeStatusTextOpen : styles.storeStatusTextClosed
                      ]}>
                        {storeStatus.isOpen ? 'Open' : 'Closed'}
                      </Text>
                    </TouchableOpacity>
                    {(vendorProfile?.borough || vendorProfile?.city) && (
                      <View style={styles.locationInline}>
                        <Feather name="map-pin" size={12} color={colors.textLight} />
                        <Text style={styles.locationTextInline}>
                          {vendorProfile?.borough || vendorProfile?.city}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
                {!isVendor && (
                  <TouchableOpacity
                    style={styles.messageButton}
                    onPress={startChat}
                    disabled={isStartingChat}
                  >
                    {isStartingChat ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Feather name="message-circle" size={18} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Description</Text>
              <View style={styles.descriptionCard}>
                <Text style={styles.description}>{product.description}</Text>
              </View>
            </View>

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Details</Text>
              <View style={styles.detailsGrid}>
                {product.brand && (
                  <View style={styles.detailCard}>
                    <Text style={styles.detailLabel}>Brand</Text>
                    <Text style={styles.detailValue}>{product.brand}</Text>
                  </View>
                )}
                {product.size && (
                  <View style={styles.detailCard}>
                    <Text style={styles.detailLabel}>Size</Text>
                    <Text style={styles.detailValue}>{product.size}</Text>
                  </View>
                )}
                {product.color && (
                  <View style={styles.detailCard}>
                    <Text style={styles.detailLabel}>Color</Text>
                    <Text style={styles.detailValue}>{product.color}</Text>
                  </View>
                )}
                <View style={styles.detailCard}>
                  <Text style={styles.detailLabel}>Condition</Text>
                  <Text style={styles.detailValue}>{product.condition ? formatDisplayValue(product.condition) : 'Not specified'}</Text>
                </View>
              </View>
            </View>

            {product.sizes && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Select Size</Text>
                <View style={styles.sizeButtons}>
                  {product.sizes.map(size => (
                    <TouchableOpacity
                      key={size}
                      style={[
                        styles.sizeButton,
                        selectedSize === size && styles.selectedSize
                      ]}
                      onPress={() => setSelectedSize(size)}
                    >
                      <Text style={[
                        styles.sizeText,
                        selectedSize === size && styles.selectedSizeText
                      ]}>
                        {size}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </View>
        </ScrollView>

        {!isVendor && (
          <View style={styles.bottomBar}>
            {Number(product.quantity ?? 0) > 1 && (
              <View style={styles.quantityContainer}>
                <View style={styles.quantityControl}>
                  <TouchableOpacity
                    onPress={() => setQuantity(q => Math.max(1, q - 1))}
                    style={[styles.quantityButton, quantity <= 1 && styles.quantityButtonDisabled]}
                    disabled={quantity <= 1}
                  >
                    <Feather name="minus" size={16} color={quantity <= 1 ? colors.textLight : colors.text} />
                  </TouchableOpacity>

                  <Text style={styles.quantityText}>{quantity}</Text>

                  <TouchableOpacity
                    onPress={() => setQuantity(q => Math.min(Number(product.quantity ?? 0), q + 1))}
                    style={[styles.quantityButton, quantity >= Number(product.quantity ?? 0) && styles.quantityButtonDisabled]}
                    disabled={quantity >= Number(product.quantity ?? 0)}
                  >
                    <Feather name="plus" size={16} color={quantity >= Number(product.quantity ?? 0) ? colors.textLight : colors.text} />
                  </TouchableOpacity>
                </View>
              </View>
            )}

            <Button
              onPress={handleAddToCart}
              loading={addToCart.isPending}
              style={styles.addToCartButton}
              disabled={(Number(product.quantity ?? 0) <= 0)}
            >
              {Number(product.quantity ?? 0) > 0 ? `Add to Cart${quantity > 1 ? ` (${quantity})` : ''}` : 'Sold Out'}
            </Button>
          </View>
        )}
      </Animated.View>

      <VendorRatingsModal
        visible={showRatingsModal}
        onClose={() => setShowRatingsModal(false)}
        vendorId={product.vendor?.id}
        storeName={product.vendor?.store_name}
      />

      {/* Store Hours Modal */}
      <Modal
        visible={showStoreHoursModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStoreHoursModal(false)}
      >
        <View style={styles.storeHoursWebModalWrapper}>
          <View style={styles.storeHoursOverlay}>
            <View style={styles.storeHoursSheetContent}>
              <View style={styles.storeHoursModalHeader}>
                <View style={styles.storeHoursHeaderLeft}>
                  <Text style={styles.storeHoursModalTitle}>Store Hours</Text>
                  {product.vendor?.store_name && (
                    <Text style={styles.storeHoursModalSubtitle}>
                      {product.vendor.store_name}
                    </Text>
                  )}
                </View>
                <TouchableOpacity
                  onPress={() => setShowStoreHoursModal(false)}
                  style={styles.storeHoursCloseButton}
                >
                  <Feather name="x" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
              <View style={styles.storeHoursBody}>
                <StoreHoursDisplay 
                  hours={vendorHoursData} 
                  vendorCity={vendorLocation.city}
                  vendorState={vendorLocation.state}
                  vendorCountry={vendorLocation.country}
                />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  imageSection: {
    width: '100%',
    height: 350,
    backgroundColor: colors.background,
    position: 'relative',
  },
  likeButton: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    width: 40,
    height: 40,
    borderRadius: 14,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  likeButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  imageContainer: {
    width: '100%',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  mainContent: {
    paddingLeft: spacing.lg + 4,
    paddingRight: spacing.lg,
    paddingTop: spacing.sm,
  },
  headerCard: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    padding: 12,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  productInfo: {
    marginBottom: 10,
  },
  headerDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginBottom: 10,
    marginHorizontal: -4,
    opacity: 0.5,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -0.5,
    marginBottom: 2,
  },
  priceContainer: {
    backgroundColor: colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  price: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000000',
    letterSpacing: -0.5,
  },
  vendorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  vendorLogo: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.background,
  },
  vendorLogoPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  vendorNameContainer: {
    flex: 1,
    marginLeft: 10,
  },
  vendorName: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -0.2,
    marginBottom: 1,
  },
  vendorSubHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  ratingInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  ratingTextInline: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.text,
  },
  storeStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 5,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  storeStatusOpen: {
    borderColor: colors.primary + '30',
  },
  storeStatusDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  storeStatusDotOpen: {
    backgroundColor: colors.primary,
  },
  storeStatusDotClosed: {
    backgroundColor: colors.error,
  },
  storeStatusText: {
    fontSize: 10,
    fontWeight: '600',
  },
  storeStatusTextOpen: {
    color: colors.primary,
  },
  storeStatusTextClosed: {
    color: colors.error,
  },
  locationInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  locationTextInline: {
    fontSize: 10,
    color: colors.textLight,
    fontWeight: '500',
  },
  messageButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  section: {
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -0.3,
    marginBottom: 6,
  },
  descriptionCard: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 10,
    borderWidth: 1,
    borderColor: colors.border,
  },
  description: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
    fontWeight: '400',
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 8,
    width: '48%',
    borderWidth: 1,
    borderColor: colors.border,
  },
  detailLabel: {
    fontSize: 10,
    color: colors.textLight,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 13,
    color: colors.text,
    fontWeight: '600',
  },
  sizeButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  sizeButton: {
    minWidth: 48,
    height: 40,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    paddingHorizontal: 12,
  },
  selectedSize: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  sizeText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  selectedSizeText: {
    color: '#000',
  },
  bottomBar: {
    backgroundColor: colors.background,
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
    paddingBottom: Platform.OS === 'ios' ? 34 : 10,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    // Floating dock style
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 10,
  },
  quantityContainer: {
    flexShrink: 0,
  },
  quantityControl: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 2,
    borderWidth: 1,
    borderColor: colors.border,
  },
  quantityButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  quantityButtonDisabled: {
    opacity: 0.3,
  },
  quantityText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
    paddingHorizontal: 10,
    minWidth: 36,
    textAlign: 'center',
  },
  addToCartButton: {
    flex: 1,
    height: 48,
    borderRadius: 16,
    marginBottom: 0,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
    backgroundColor: colors.background,
  },
  errorText: {
    fontSize: 16,
    color: colors.error,
    fontWeight: '500',
  },
  fallbackImage: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagination: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  paginationDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  paginationDotActive: {
    backgroundColor: colors.primary,
    width: 20,
    height: 6,
    borderRadius: 3,
  },
  storeHoursWebModalWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      maxWidth: 430,
      alignSelf: 'center',
      width: '100%',
    } : {}),
  },
  storeHoursOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  storeHoursSheetContent: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '80%',
  },
  storeHoursModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  storeHoursHeaderLeft: {
    flex: 1,
  },
  storeHoursModalTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -0.5,
  },
  storeHoursModalSubtitle: {
    fontSize: 14,
    color: colors.textLight,
    marginTop: 2,
    fontWeight: '500',
  },
  storeHoursCloseButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
  },
  storeHoursBody: {
    padding: spacing.lg,
  },
}); 